import { supabase } from '../../../lib/supabase';
import { Folder, FolderWithClasses, FolderTreeItem } from './types';

// Fetch all folders (categories) for the current user
export const fetchFolders = async (): Promise<Folder[]> => {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .order('priority')
    .order('name');
  
  if (error) throw error;
  return data || [];
};

// Fetch folders (categories) with class counts
export const fetchFoldersWithClassCounts = async (): Promise<FolderWithClasses[]> => {
  const { data, error } = await supabase
    .from('categories')
    .select(`
      *,
      class_categories (
        class_id
      )
    `)
    .order('priority')
    .order('name');
  
  if (error) throw error;
  
  return (data || []).map(folder => ({
    ...folder,
    class_count: folder.class_categories ? folder.class_categories.length : 0
  }));
};

// Create a folder (category)
export const createFolder = async (
  name: string, 
  imgUrl: string = '',
  priority: number = 0
): Promise<Folder> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No authenticated user');

    // Create insert object without img_url initially
    const insertData: any = {
      name,
      user_id: user.id,
      priority
    };
    
    // Only add img_url if it's a non-empty string
    if (typeof imgUrl === 'string' && imgUrl.trim() !== '') {
      insertData.img_url = imgUrl.trim();
    }
    
    // Insert without requiring data to be returned
    const { error } = await supabase
      .from('categories')
      .insert(insertData);
    
    if (error) {
      console.error('Error creating category:', error);
      throw error;
    }
    
    // Try to get the newly created folder
    // Since we don't have the generated ID yet, we need to query by other attributes
    const { data: newFolders, error: fetchError } = await supabase
      .from('categories')
      .select('*')
      .eq('name', name)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1);
    
    if (fetchError) {
      console.error('Error fetching created category:', fetchError);
      throw fetchError;
    }
    
    // If we still couldn't get the data, create a basic object with a temporary ID
    if (!newFolders || newFolders.length === 0) {
      console.log('Category created but could not fetch details. Returning basic info.');
      return {
        id: 'temp-' + Date.now(), // Temporary ID
        name,
        img_url: imgUrl && imgUrl.trim() !== '' ? imgUrl.trim() : null,
        priority,
        user_id: user.id
      } as Folder;
    }
    
    return newFolders[0];
  } catch (error) {
    console.error('Error in createFolder:', error);
    throw error;
  }
};

// Update a folder (category)
export const updateFolder = async (
  id: string,
  name: string,
  imgUrl: string = '',
  priority?: number
): Promise<Folder> => {
  try {
    // Create update object
    const updateData: any = {
      name
    };
    
    // Include priority if provided
    if (typeof priority === 'number') {
      updateData.priority = priority;
    }
    
    console.log('Updating folder with data:', { id, updateData });
    
    // Handle image URL - only set if it has a value
    if (typeof imgUrl === 'string' && imgUrl.trim() !== '') {
      updateData.img_url = imgUrl.trim();
    }
    
    // Perform the update AND get the updated row in a single operation
    const { data: directUpdateResult, error } = await supabase
      .from('categories')
      .update(updateData)
      .eq('id', id)
      .select();
    
    console.log('Direct Supabase update response:', { directUpdateResult, error });
    
    if (error) {
      console.error('Error updating category:', error);
      throw error;
    }
    
    // If we got the updated data directly, return it
    if (directUpdateResult && directUpdateResult.length > 0) {
      console.log('Successfully updated category with direct result:', directUpdateResult[0]);
      return directUpdateResult[0];
    }
    
    // Fetch the updated category to return
    const { data: updatedData, error: fetchError } = await supabase
      .from('categories')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    
    if (fetchError) {
      console.error('Error fetching updated category:', fetchError);
      throw fetchError;
    }
    
    // If we still couldn't get the data, create a basic object to return
    if (!updatedData) {
      console.log('Category updated but could not fetch details. Returning basic info.');
      return {
        id,
        name,
        img_url: imgUrl && imgUrl.trim() !== '' ? imgUrl.trim() : null,
        priority: typeof priority === 'number' ? priority : 0,
        user_id: '' // We don't know this but need to satisfy the type
      } as Folder;
    }
    
    return updatedData;
  } catch (error) {
    console.error('Error in updateFolder:', error);
    throw error;
  }
};

// Delete a folder (category)
export const deleteFolder = async (
  id: string,
  moveClassesToParent: boolean = true
): Promise<void> => {
  try {
    // No need to get parent ID as that column doesn't exist
    
    // Note: moveClassesToParent parameter is kept for backward compatibility
    // but functionality is removed since there's no parent-child relationship
    
    // Delete class associations for this folder
    await supabase
      .from('class_categories')
      .delete()
      .eq('category_id', id);
    
    // No parent-child folder relationship to update
    
    // Finally delete the folder
    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  } catch (error) {
    throw error;
  }
};

// Get classes for a specific folder (category)
export const getClassesByFolder = async (folderId: string | null) => {
  if (folderId === null) {
    // Get all classes
    const { data, error } = await supabase
      .from('classes')
      .select(`
        *,
        class_categories (
          category_id
        )
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return (data || []).map(class_ => ({
      ...class_,
      categories: class_.class_categories ? class_.class_categories.map((cc: any) => cc.category_id) : []
    }));
  } else {
    // Get classes for the specified folder (category)
    try {
      const { data, error } = await supabase
        .from('class_categories')
        .select(`
          position,
          classes (
            *,
            class_categories (
              category_id
            )
          )
        `)
        .eq('category_id', folderId)
        .order('position', { ascending: true, nullsFirst: false })
        .order('classes(created_at)', { ascending: false });

      if (error) throw error;

      return (data || []).map((item: any) => {
        const class_ = item.classes;
        if (!class_) return null;
        const classObj = class_ as any;
        return {
          ...classObj,
          categories: classObj.class_categories ? classObj.class_categories.map((cc: any) => cc.category_id) : []
        };
      }).filter(Boolean);
    } catch (e) {
      console.warn('position column missing or query failed; falling back to created_at ordering', e);
      const { data, error } = await supabase
        .from('class_categories')
        .select(`
          classes (
            *,
            class_categories (
              category_id
            )
          )
        `)
        .eq('category_id', folderId)
        .order('classes(created_at)', { ascending: false });

      if (error) throw error;

      return (data || []).map((item: any) => {
        const class_ = item.classes;
        if (!class_) return null;
        const classObj = class_ as any;
        return {
          ...classObj,
          categories: classObj.class_categories ? classObj.class_categories.map((cc: any) => cc.category_id) : []
        };
      }).filter(Boolean);
    }
  }
};

// Build a folder tree structure from flat folders array
export const buildFolderTree = (
  folders: FolderWithClasses[]
): FolderTreeItem[] => {
  // Simplified version without parent-child relationships
  return folders
    .map(folder => ({
      folder,
      children: [], // No children since there's no parent-child relationship
      level: 0
    }));
};
